import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { AlertService } from '../../../../services/alert.service';
import { CompaniesService } from '../../../../services/companies.service';
import { AccountsService } from '../../../../services/accounts.service';
import { TemplatesService } from '../../../../services/templates.service';
import { FormValues, RichCards, RichCard_Carousel, ContentInfo } from '../interfaces/interfaces';
import Header from '../../../../components/layout/header.component';
import useRCSStructures from '../../../../hooks/use-structure-templates.hook';
import Asaid from '../../../../components/layout/asaid.component';
import Footer from '../../../../components/layout/footer.component';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import Select from 'react-select';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import Input from '../../../../components/forms/input.component';
import Button from '../../../../components/forms/button.component';
import Icon from '../../../../components/forms/icon.component';
import Title from '../../../../components/forms/titles.component';
import AndroidMockup from '../../../../components/mockups/android.component';
import SimpleTitle from '../../../../components/forms/simple-titles.component';
import Tooltip from '../../../../components/forms/tooltip.component';
import LogData from '../../../../components/logs/log-data.component';
import Navigate from '../../../../components/cards/header-navigate.component';
import Disclaimer from '../../../../components/cards/video-modal.componenet';
import { useSession } from '../../../../hooks/use-session.hook';
import { PROFILES } from '../../../../constants';
import RichCard from '../components/rich-card.components';
import CarrouselRichCard from '../components/rich-card-carrousel.component';
import Media from '../components/content-info.components';
import SugestionsGeneral from '../components/suggestions-general.component';
import { extractPreviewFromTemplate } from '../../rcs/templates-rcs.page';
import React from 'react';

declare global {
  interface Window {
    bootstrap?: any;
  }
}

function BuilderRCSSingleConversational() {
  useBgPages();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { session, selectedAccount } = useSession();
  const { payload } = location.state || {};
  const { data } = location.state || {};
  useEffect(() => {
    const typeFromPayload = payload?.rows?.type;
    const typeFromData = data?.type;

    if (!typeFromPayload && !typeFromData) {
      AlertService.info(
        '¡Atención!',
        "Para acceder al editor de RCS, selecciona el botón de acciones de una plantilla y haz clic en 'Editar' o en el botón 'Crear plantilla' y selecciona el tipo de plantilla que deseas crear."
      );
      navigate('/templates-panel/templates-rcs');
    }
  }, [payload, data, navigate]);
  if (!payload?.rows?.type && !data?.type) {
    return null;
  }
  const { createCMStructure } = useRCSStructures();
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [accountOptions, setAccountOptions] = useState<any[]>([]);
  const [activeActions, setActiveActions] = useState<{ [key: string]: string }>({});
  const [counts, setCounts] = useState({
    richCard: 0,
    richCard_Carrosuel: 0,
    contenInfo: 0,
    total: 0
  });
  // React Forms 📌

  const defaultValues: FormValues = {
    _id: '',
    type: payload?.rows.type || data.type,
    name: '',
    companie: '',
    account: '',
    elements: [],
    suggestions: []
  };

  const methods = useForm({
    mode: 'onChange',
    defaultValues
  });

  const {
    register,
    watch,
    reset,
    control,
    getValues,
    setValue,
    trigger,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = methods;

  const elementItems = watch();

  // Elementos 📌
  const handleAddElement = (type: string) => {
    if (type === 'richCard') {
      const newElement: RichCards = {
        type,
        URLImage: '',
        URLImagePoster: '',
        URLVideo: '',
        title: '',
        description: '',
        gnlLabel: '',
        linkURL: '',
        phoneNumber: '',
        label: '',
        latitude: 0,
        longitude: 0,
        startTime: '',
        endTime: '',
        titleC: '',
        descriptionC: '',
        typeAction: '',
        imageCheck: true,
        videoCheck: false,
        suggestedReply: false,
        suggestedAction: false
      };

      const currentElements = getValues('elements') || [];
      const updatedElements = [...currentElements, newElement];

      setValue('elements', updatedElements);
    }
    if (type === 'contenInfo') {
      const newElement: ContentInfo = {
        type,
        URLImage: '',
        URLImagePoster: '',
        URLVideo: '',
        URLFile: '',
        imageCheck: true,
        videoCheck: false,
        fileCheck: false,
        forceRefresh: false
      };
      const currentElements = getValues('elements') || [];
      const updatedElements = [...currentElements, newElement];

      setValue('elements', updatedElements);
    }

    if (type === 'richCard_Carrosuel') {
      const newElement: RichCard_Carousel = {
        type,
        cards: [
          {
            type: 'richCard',
            URLImage: '',
            URLImagePoster: '',
            URLVideo: '',
            title: '',
            description: '',
            gnlLabel: '',
            linkURL: '',
            phoneNumber: '',
            imageCheck: true,
            videoCheck: false,
            suggestedReply: false,
            suggestedAction: false
          },
          {
            type: 'richCard',
            URLImage: '',
            URLImagePoster: '',
            URLVideo: '',
            title: '',
            description: '',
            gnlLabel: '',
            linkURL: '',
            phoneNumber: '',
            imageCheck: true,
            videoCheck: false,
            suggestedReply: false,
            suggestedAction: false
          }
        ]
      };
      const currentElements = getValues('elements') || [];
      const updatedElements = [...currentElements, newElement];

      setValue('elements', updatedElements);
    }
  };

  const countElements = (elementItems: any[]) => {
    return elementItems.reduce(
      (acc, item) => {
        if (item.type === 'richCard') {
          acc.richCard += 1;
        } else if (item.type === 'richCard_Carrosuel') {
          acc.richCard_Carrosuel += 1;
        } else if (item.type === 'contenInfo') {
          acc.contenInfo += 1;
        }
        acc.total += 1;
        return acc;
      },
      { richCard: 0, richCard_Carrosuel: 0, contenInfo: 0, total: 0 }
    );
  };

  // Obtener datos de empresa y cuentas 📌

  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      setCompanies(
        data.map((item: any) => ({
          value: item._id,
          label: item?.name,
          type: item.service_type
        }))
      );
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setCompanies([]);
    }
  };

  const getAccount = async () => {
    if (selectedCompany) {
      try {
        const data = await AccountsService.getAccountsForCompany(selectedCompany);
        setAccountOptions(
          data.map((item: any) => ({
            value: item?._id,
            label: item?.name,
            type: item.service_type
          }))
        );
      } catch (errorCode: any) {
        console.log('Error:', errorCode);
        AlertService.toastError(errorCode.error || errorCode);
        setAccountOptions([]);
      }
    }
  };

  // Envio de formulario 📌

  const submit = async (data: any, buttonId: string) => {
    if (counts.total === 0) {
      AlertService.toastInfo(`¡ Atención !, debes agregar al menos un elemento a tu plantilla.`);
      return;
    }

    try {
      const structure = await createCMStructure(data);
      const addTemplate = {
        account: data.account,
        name: data?.name,
        type: data.type.toUpperCase(),
        [data.type.toLowerCase()]: structure,
        suggestions: data.suggestions
      };
      const editTemplate = {
        _id: payload?.rows?._id,
        type: payload?.rows.type.toUpperCase(),
        name: data?.name,
        account: data.account,
        [data.type.toLowerCase()]: structure,
        suggestions: data.suggestions
      };

      const confirmed = await AlertService.question(
        t('general.atention'),
        `¿Deseas ${payload?.rows?._id ? 'editar' : 'dar de alta'} esta plantilla, ${
          payload?.rows?._id ? payload?.rows?.name : data?.name
        }?`,
        null as any
      );
      if (!confirmed) return;
      const isEditing = Boolean(payload?.rows?._id);
      const templateData = isEditing ? editTemplate : addTemplate;
      delete templateData.suggestions;
      await (isEditing
        ? TemplatesService.editTemplateRCS(templateData)
        : TemplatesService.addTemplateRCS(templateData));
      AlertService.toastSuccess(
        `¡ Éxito !, tu plantilla se ${isEditing ? 'actualizó' : 'dio de alta'} correctamente.`
      );
      LogData(templateData, buttonId, window.location.href);
      reset(defaultValues);
      navigate('/templates-panel/templates-rcs');
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getSuggestionsFromTemplate = (template: any) => {
    if (template.type === 'BASIC') {
      return defaultValues.suggestions;
    }

    if (template.preview?.[0]?.suggestions && template.preview[0].suggestions.length > 0) {
      return template.preview[0].suggestions;
    }

    const processActionOrSuggestion = (item: any) => {
      if (item.reply) {
        return {
          type: 'answer',
          gnlLabel: item.reply.text || '',
          linkURL: '',
          phoneNumber: '',
          label: '',
          latitude: 0,
          longitude: 0,
          startTime: '',
          endTime: '',
          titleC: '',
          descriptionC: ''
        };
      }

      if (item.action) {
        const action = item.action;

        if (action.openUrlAction) {
          return {
            type: 'redirect',
            gnlLabel: action.text || '',
            linkURL: action.openUrlAction.url || '',
            phoneNumber: '',
            label: '',
            latitude: 0,
            longitude: 0,
            startTime: '',
            endTime: '',
            titleC: '',
            descriptionC: ''
          };
        }

        if (action.dialAction) {
          return {
            type: 'call',
            gnlLabel: action.text || '',
            linkURL: '',
            phoneNumber: action.dialAction.phoneNumber || '',
            label: '',
            latitude: 0,
            longitude: 0,
            startTime: '',
            endTime: '',
            titleC: '',
            descriptionC: ''
          };
        }

        if (action.viewLocationAction) {
          return {
            type: 'location',
            gnlLabel: action.text || '',
            linkURL: '',
            phoneNumber: '',
            label: action.viewLocationAction.label || '',
            latitude: action.viewLocationAction.latLong?.latitude || 0,
            longitude: action.viewLocationAction.latLong?.longitude || 0,
            startTime: '',
            endTime: '',
            titleC: '',
            descriptionC: ''
          };
        }

        if (action.createCalendarEventAction) {
          return {
            type: 'calendar',
            gnlLabel: action.text || '',
            linkURL: '',
            phoneNumber: '',
            label: '',
            latitude: 0,
            longitude: 0,
            startTime: action.createCalendarEventAction.startTime || '',
            endTime: action.createCalendarEventAction.endTime || '',
            titleC: action.createCalendarEventAction.title || '',
            descriptionC: action.createCalendarEventAction.description || ''
          };
        }

        return {
          type: 'redirect',
          gnlLabel: action.text || '',
          linkURL: '',
          phoneNumber: '',
          label: '',
          latitude: 0,
          longitude: 0,
          startTime: '',
          endTime: '',
          titleC: '',
          descriptionC: ''
        };
      }

      return null;
    };

    if (template.single && Array.isArray(template.single)) {
      for (const singleElement of template.single) {
        if (singleElement?.suggestions && singleElement.suggestions.length > 0) {
          return singleElement.suggestions.map(processActionOrSuggestion).filter(Boolean);
        }
      }
    }

    if (template.conversational && Array.isArray(template.conversational)) {
      for (const conversationalElement of template.conversational) {
        if (conversationalElement?.suggestions && conversationalElement.suggestions.length > 0) {
          return conversationalElement.suggestions.map(processActionOrSuggestion).filter(Boolean);
        }
      }
    }

    return defaultValues.suggestions;
  };

  useEffect(() => {
    getCompanies();
  }, []);

  useEffect(() => {
    if (session.profile === PROFILES.ADMIN) {
      setValue('account', selectedAccount.account_id);
    }
  }, [selectedAccount, session]);

  useEffect(() => {
    getAccount();
  }, [selectedCompany]);

  useEffect(() => {
    if (watch('elements')) {
      setCounts(countElements(watch('elements')));
    }
  }, [watch('elements')]);

  useEffect(() => {
    if (payload?.rows) {
      const getElementsFromTemplate = (template: any) => {
        return extractPreviewFromTemplate(template);
      };

      const elements = getElementsFromTemplate(payload.rows);
      const suggestions = getSuggestionsFromTemplate(payload.rows);

      reset({
        _id: payload.rows._id || defaultValues._id,
        type: payload.rows.type || defaultValues.type,
        name: payload.rows.name || defaultValues.name,
        companie: payload.rows.account?.company?._id || defaultValues.companie,
        account: payload.rows.account?._id || defaultValues.account,
        elements: elements.filter((el: any): el is NonNullable<typeof el> => el !== null),
        suggestions: suggestions
      });

      setSelectedCompany(payload.rows.account?.company?._id || defaultValues.companie);

      const initialActions: { [key: string]: string } = {};
      elements.forEach((el: any, i: number) => {
        if (el?.type === 'richCard' && el?.typeAction) {
          initialActions[`${i}`] = el.typeAction;
        }
        if (el?.type === 'richCard_Carrosuel' && Array.isArray(el.cards)) {
          el.cards.forEach((card: any, j: number) => {
            if (card?.typeAction) {
              initialActions[`${i}-${j}`] = card.typeAction;
            }
          });
        }
      });
      setActiveActions(initialActions);
    }
  }, [payload?.rows]);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Asaid />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-xxl">
              <Navigate
                btnBack
                title={payload?.rows?._id ? 'Edita tu plantilla' : 'Crea tu plantilla'}
                titleBolder={
                  payload?.rows?._id
                    ? payload.rows?.name
                    : data.type === 'single'
                      ? 'RCS Simple'
                      : 'RCS Conversacional'
                }
                subtitle={
                  payload?.rows?._id
                    ? `${payload?.rows.account?.company?.name} - ${payload?.rows.account?.name}`
                    : 'Ingresa los datos necesarios para tu mensaje RCS básico.'
                }
                breadcrumbItems={['Herramientas', 'Plantillas', 'Editor']}
              />
              <FormProvider {...methods}>
                <form
                  onSubmit={handleSubmit((data) => {
                    const isValid = trigger();
                    const elements = watch('elements') || [];

                    if (!isValid) {
                      AlertService.toastInfo(
                        '¡ Atención ! Hay campos obligatorios que debes llenar.'
                      );
                      return;
                    }

                    const isEmptyRichCard = (card: any) =>
                      card.type === 'richCard' &&
                      card.URLImage === '' &&
                      card.URLImagePoster === '' &&
                      card.URLVideo === '' &&
                      card.title === '' &&
                      card.description === '' &&
                      card.gnlLabel === '' &&
                      card.linkURL === '' &&
                      !card.imageCheck &&
                      !card.videoCheck;

                    const hasEmptyRichCard = elements.some(isEmptyRichCard);

                    if (hasEmptyRichCard) {
                      AlertService.toastInfo(
                        '¡Atención! En una tarjeta enriquecida debes agregar al menos el título, el mensaje o la etiqueta de redireccionamiento.'
                      );
                      return;
                    }

                    const hasEmptyCarouselCard = elements.some(
                      (el: any) =>
                        el.type === 'richCard_Carrosuel' &&
                        Array.isArray(el.cards) &&
                        el.cards.some(isEmptyRichCard)
                    );

                    if (hasEmptyCarouselCard) {
                      AlertService.toastInfo(
                        '¡Atención! En las tarjetas del carrusel debes agregar al menos el título, el mensaje o la etiqueta de redireccionamiento.'
                      );
                      return;
                    }

                    submit(
                      data,
                      payload?.rows?._id
                        ? `editTemplate_${payload?.rows.type}-7333456789`
                        : `addTemplate${data.type}-7232324567`
                    );
                  })}
                >
                  <div className="card mb-xl-10 animate-fadeInUp animate-delay-1 custom-index mb-10">
                    <div className="card-body">
                      <Title
                        icon="ki-notepad-edit"
                        title="Datos generales"
                        subtitle="Ingresa el nombre y la cuenta a donde se asignará tu plantilla."
                        type="primary"
                      />
                      <div className="flex-column current">
                        <div className="row">
                          {session.profile === PROFILES.ROOT && !payload?.rows?._id && (
                            <div className="col-12 col-md-4">
                              <div className="fv-row mb-7">
                                <label className="form-label">
                                  {t('blacklist-page.company-label')}
                                  <span className="fs-8 text-muted ms-1 required"></span>
                                </label>
                                <Controller
                                  control={control}
                                  name="companie"
                                  rules={{ required: true }}
                                  render={({
                                    field: { onChange, value },
                                    fieldState: { error }
                                  }) => (
                                    <>
                                      <Select
                                        options={companies}
                                        noOptionsMessage={({ inputValue }) =>
                                          inputValue
                                            ? t('general.no-options', { inputValue })
                                            : t('general.no-options')
                                        }
                                        className={
                                          error
                                            ? 'form-select-custom is-invalid'
                                            : 'form-select-custom'
                                        }
                                        classNamePrefix="form-select-custom"
                                        placeholder={t('blacklist-page.company-placeholder')}
                                        onChange={(selectedOption) => {
                                          const selectedValue = selectedOption
                                            ? selectedOption.value
                                            : '';
                                          setSelectedCompany(selectedValue);
                                          onChange(selectedValue);
                                        }}
                                        value={
                                          companies.find((option: any) => option.value === value) ||
                                          null
                                        }
                                      />
                                      <InvalidFeedback error={error} />
                                    </>
                                  )}
                                />
                              </div>
                            </div>
                          )}
                          {session.profile === PROFILES.ROOT && (
                            <div className="col-12 col-md-4">
                              <div className="fv-row mb-7">
                                <div className="d-flex justify-content-between">
                                  <label className="form-label required me-3">Cuentas</label>
                                  {accountOptions.length > 0 && (
                                    <Link
                                      to="/accounts"
                                      className="ms-3 text-primary text-hover-primary fw-bold mt-3"
                                    >
                                      Crear cuenta
                                    </Link>
                                  )}
                                </div>
                                <Controller
                                  control={control}
                                  name="account"
                                  rules={{ required: true }}
                                  render={({
                                    field: { onChange, value },
                                    fieldState: { error }
                                  }) => (
                                    <>
                                      <Select
                                        options={accountOptions}
                                        isClearable
                                        noOptionsMessage={() => (
                                          <div className="empty-select">
                                            <Link
                                              className="text-primary text-hover-primary"
                                              to="/accounts"
                                            >
                                              <Icon
                                                icon="ki-abstract-10"
                                                size="fs-3"
                                                type="primary"
                                                className="mt-1 me-2"
                                              />
                                              Agregar cuenta
                                            </Link>
                                          </div>
                                        )}
                                        className={
                                          error
                                            ? 'form-select-custom is-invalid'
                                            : 'form-select-custom'
                                        }
                                        classNamePrefix="form-select-custom"
                                        placeholder="Selecciona cuenta disponible"
                                        onChange={(selectedOption) => {
                                          onChange(selectedOption?.value);
                                        }}
                                        value={
                                          accountOptions.find(
                                            (option: any) => option.value === value
                                          ) || null
                                        }
                                      />
                                      <InvalidFeedback error={error} />
                                    </>
                                  )}
                                />
                              </div>
                            </div>
                          )}
                          <div className="col-12 col-md-4">
                            <div className="fv-row mb-0">
                              <Input
                                label="Nombre de plantilla"
                                requiredIndicator="required"
                                placeholder="Ingresa nombre de tu plantilla"
                                errors={errors?.name}
                                {...register('name', {
                                  required: true,
                                  setValueAs: (value) => value.trim()
                                })}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row g-xl-10">
                    <div className="col-xl-8 mb-5">
                      <div className="card mb-5 mb-xxl-8">
                        <div className="card-body">
                          <div className="align-items-center border-0 mt-5">
                            <Title
                              icon="ki-abstract-24"
                              title={`Elementos para tu plantilla`}
                              subtitle="Agrega los elementos necesarios a tu plantilla."
                              type="info"
                            />
                            <div className="mb-8 bg-default p-3 rounded-12 d-flex justify-content-start">
                              <div className="badge badge-lg badge-light-primary rounded-12">
                                <Tooltip
                                  title={
                                    'Al hacer clic, agregarás una tarjeta enriquecida y podrás añadir tantas como necesites.'
                                  }
                                  className="width-fit-content d-inline-block cursor-pointer"
                                >
                                  <div
                                    className="rounded-12 align-items-center p-3 hover-scale"
                                    onClick={() => handleAddElement('richCard')}
                                  >
                                    <div className="d-flex align-items-center cursor-pointer">
                                      <Icon
                                        icon="ki-element-9"
                                        size="fs-1"
                                        className="fw-bolder"
                                        type="dark"
                                      />
                                      <span className="fw-bolder ms-1">
                                        <span className="d-none d-md-inline-block">
                                          Tarjeta enriquecida{' '}
                                        </span>
                                        {counts.richCard > 0 && (
                                          <span className="symbol-badge badge badge-circle bg-info text-white start-100 fw-bolder ms-3">
                                            {counts.richCard}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </Tooltip>
                                <span className="text-primary fw-bolder">|</span>
                                <Tooltip
                                  title={
                                    'Al hacer clic, agregarás un carrusel con 2 tarjetas por defecto y agrega hasta 10 tarjetas en total. Agrega los carruseles necesarios.'
                                  }
                                  className="width-fit-content d-inline-block cursor-pointer"
                                >
                                  <div
                                    className="rounded-12 align-items-center p-3 hover-scale"
                                    onClick={() => handleAddElement('richCard_Carrosuel')}
                                  >
                                    <div className="d-flex align-items-center cursor-pointer">
                                      <Icon
                                        icon="ki-slider-horizontal"
                                        size="fs-1"
                                        className="fw-bolder"
                                        type="dark"
                                      />
                                      <span className="fw-bolder ms-1">
                                        <span className="d-none d-md-inline-block">
                                          Carruseles{' '}
                                        </span>
                                        {counts.richCard_Carrosuel > 0 && (
                                          <span className="symbol-badge badge badge-circle bg-info text-white start-100 fw-bolder ms-3">
                                            {counts.richCard_Carrosuel}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </Tooltip>
                                <span className="text-primary fw-bolder">|</span>
                                <Tooltip
                                  title={
                                    'Al hacer clic, añadirás un listado de campos para enviar archivos, imágenes o videos.'
                                  }
                                  className="width-fit-content d-inline-block cursor-pointer"
                                >
                                  <div
                                    className="rounded-12 align-items-center p-3 hover-scale"
                                    onClick={() => handleAddElement('contenInfo')}
                                  >
                                    <div className="d-flex align-items-center cursor-pointer">
                                      <Icon
                                        icon="ki-file-sheet"
                                        size="fs-1"
                                        className="fw-bolder"
                                        type="dark"
                                      />
                                      <span className="fw-bolder ms-1">
                                        <span className="d-none d-md-inline-block">Media </span>
                                        {counts.contenInfo > 0 && (
                                          <span className="symbol-badge badge badge-circle bg-info text-white start-100 fw-bolder ms-3">
                                            {counts.contenInfo}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </Tooltip>
                              </div>
                            </div>

                            <div className="separator separator-dashed text-dark my-5"></div>

                            <div className="scroll mh-1100px">
                              {watch(`elements`).length !== 0 ? (
                                (watch('elements') || []).map((element, index) => {
                                  const actionCounts = {
                                    redirect: 0,
                                    call: 0,
                                    location: 0,
                                    calendar: 0,
                                    answer: 0
                                  };

                                  element.actions?.forEach((action: any) => {
                                    if (actionCounts.hasOwnProperty(action.type)) {
                                      actionCounts[action.type as keyof typeof actionCounts]++;
                                    }
                                  });
                                  switch (element.type) {
                                    case 'richCard':
                                      return (
                                        <React.Fragment key={index}>
                                          <RichCard
                                            type={element.type}
                                            index={index}
                                            actionCounts={actionCounts}
                                            actionElement={element}
                                            activeActions={activeActions}
                                          />
                                        </React.Fragment>
                                      );
                                    case 'richCard_Carrosuel':
                                      return (
                                        <React.Fragment key={index}>
                                          <CarrouselRichCard
                                            index={index}
                                            actionElement={element}
                                          />
                                        </React.Fragment>
                                      );
                                    case 'contenInfo':
                                      return (
                                        <React.Fragment key={index}>
                                          <Media
                                            index={index}
                                            actionElement={element}
                                            actionCounts={actionCounts}
                                          />
                                        </React.Fragment>
                                      );
                                    default:
                                      return null;
                                  }
                                })
                              ) : (
                                <div className="text-center p-4">
                                  <img
                                    alt="imageCh"
                                    title="empty-table"
                                    className="mb-5 animated-Alert"
                                    src="/assets/media/illustrations/undraw/forms-rcs.svg"
                                    width="40%"
                                  />
                                  <p className="mb-0 fs-3 text-muted">
                                    No has agregado ningún elemento.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                          <SugestionsGeneral />
                        </div>
                        <div className="card-footer">
                          <div className="d-flex justify-content-between align-items-center">
                            {counts.total > 0 &&
                              (watch('type').toLowerCase() === 'conversational' ? (
                                <SimpleTitle
                                  title={
                                    <>
                                      <div className="fw-bold fs-2">
                                        Elementos utilizados:{' '}
                                        <span className="text-danger fw-bolder fs-2">
                                          {counts.total} <br />
                                        </span>
                                      </div>
                                      <Disclaimer
                                        nameVideo={t('video.rcs-disclaimer')}
                                        titleLink="condiciones"
                                      />
                                    </>
                                  }
                                />
                              ) : (
                                <SimpleTitle
                                  title={
                                    <span className="fw-bold fs-2">
                                      Costo de envío de plantilla:
                                      <span className="text-danger fw-bolder ms-2 fs-2">
                                        {counts.total} <span className="text-danger">créditos</span>
                                      </span>
                                    </span>
                                  }
                                  subtitle="Este costo se cobrará por el total de elementos agregados a esta plantilla."
                                />
                              ))}
                            <Button
                              type="submit"
                              typeStepperBtn="submit"
                              id="sendData"
                              className="btn btn-lg btn-primary w-230px hover-scale mb-10"
                              isLoading={isSubmitting}
                              disabled={!isValid || counts.total === 0}
                            >
                              <span className="d-none d-md-inline-block">{t('general.save')}</span>
                              <Icon icon="ki-send" size="fs-2" className="ms-2" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl-4">
                      <div className="ms-10 sticky-box">
                        <AndroidMockup type={payload?.rows.type || data.type} data={elementItems} />
                      </div>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default BuilderRCSSingleConversational;
